/**
 * Remembers the consultation the doctor is currently in.
 *
 * Leaving the call screen does not end the call — `use-video-call` only releases
 * media on unmount and never touches the backend session, so the room, the
 * patient and the CallSession all survive a click on "Bookings". What was
 * missing was any way back: the doctor had to find the appointment again, and
 * the button there says "Start video call", which reads like starting over.
 *
 * This is the record the shell's "Return to consultation" bar is drawn from.
 *
 * `sessionStorage`, not `localStorage`: an in-progress call belongs to the tab
 * it is running in. A second tab or tomorrow's session must not inherit a
 * banner pointing at a call that is long over.
 *
 * The store is advisory. The backend's CallSession is the truth — a stale record
 * here costs one navigation, because the call screen resolves the real state on
 * mount and clears this if there is nothing to resume.
 */

const KEY = "healnova.doctor.activeCall";

export interface ActiveCall {
  bookingId: string;
  patientName: string;
  startedAt: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

/*
 * useSyncExternalStore compares snapshots by identity and re-renders forever if
 * getSnapshot returns a fresh object each call — so the parsed value is cached
 * and only rebuilt when the underlying string actually changes.
 */
let cachedRaw: string | null = null;
let cached: ActiveCall | null = null;

function readStorage(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    // Private mode or a blocked storage partition. Degrades to no banner.
    return null;
  }
}

export function getActiveCall(): ActiveCall | null {
  const raw = readStorage();
  if (raw === cachedRaw) return cached;
  cachedRaw = raw;
  try {
    cached = raw ? (JSON.parse(raw) as ActiveCall) : null;
  } catch {
    cached = null;
  }
  return cached;
}

/** SSR and the first client render must agree; there is no call on the server. */
export const getActiveCallServerSnapshot = (): ActiveCall | null => null;

export function subscribeActiveCall(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const emit = () => listeners.forEach((listener) => listener());

export function setActiveCall(call: Omit<ActiveCall, "startedAt">): void {
  const existing = getActiveCall();
  // Re-marking the same call must not restart its timer or wake every listener.
  if (existing?.bookingId === call.bookingId) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...call, startedAt: new Date().toISOString() }));
  } catch {
    return;
  }
  emit();
}

/**
 * Clears the record.
 *
 * `bookingId` guards against a stale screen clearing a banner for a different,
 * genuinely active call — pass the booking the caller is speaking for.
 */
export function clearActiveCall(bookingId?: string): void {
  const existing = getActiveCall();
  if (!existing) return;
  if (bookingId && existing.bookingId !== bookingId) return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    return;
  }
  emit();
}
